import { getSortedRowModel, getCoreRowModel, useReactTable, SortingState } from '@ui/components';
import { getOpensearchTableConfig, getOpensearchTableData } from '@api/opensearch';
import { OpensearchSearchRequestOpenSearchDataTypeEnum } from '@interfaces/models';
import { useAutoColumns } from '@shared/components';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getTableSort } from '@shared/utils';
import { useParams } from 'react-router-dom';
import { useState, useMemo } from 'react';

export interface TransactionsHistoryFilters {
  dateFrom?: string;
  dateTo?: string;
}

export const useCostCenterTransactionsHistory = (filters?: TransactionsHistoryFilters) => {
  const { t } = useTranslation();
  const { costCenterId } = useParams();

  const [sorting, setSorting] = useState<SortingState>([]);

  const { data: tableConfig, isLoading: isTableConfigLoading } = useQuery({
    queryKey: ['cost-center-transactions-history-table-config'],
    queryFn: () =>
      getOpensearchTableConfig(
        OpensearchSearchRequestOpenSearchDataTypeEnum.TRANSACTION_HISTORY_SHORT,
      ),
    enabled: !!costCenterId,
  });

  const { data: history, isLoading } = useQuery({
    queryKey: ['cost-center-transactions-history', filters],
    queryFn: () =>
      getOpensearchTableData({
        openSearchDataType: OpensearchSearchRequestOpenSearchDataTypeEnum.TRANSACTION_HISTORY_SHORT,
        costCenterId: costCenterId!,
        dateFrom: filters?.dateFrom,
        dateTo: filters?.dateTo,
        sort: getTableSort(sorting),
        pageSize: 10,
      }),
    enabled: !!costCenterId,
  });

  const { columnDefinitions } = useAutoColumns({
    columns: tableConfig || [],
  });

  const data = useMemo(() => {
    return history?.data || [];
  }, [history]);

  const table = useReactTable({
    data,
    columns: columnDefinitions,
    getRowId: (row) => row[0] as string,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
  });

  return { table, isLoading, t, isTableConfigLoading, columnsLength: columnDefinitions.length };
};
